import { gql, useMutation, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { User } from '../../../context/AuthProvider';
import { ColorOption } from '../components/Dropdown';

interface NoteDto {
  id: string;
  data: any;
  content: { id: string };
  user: { id: string };
  comment: string;
  updatedAt: string;
}

const LOCAL_STORAGE_SHOW_NOTES_KEY = 'show-note-taking';

let globalIsNoteDrawerOpen = false;
let callback: ((newState: boolean) => void) | undefined;

export const globalSetIsNoteDrawerOpen = (newState: boolean) => {
  globalIsNoteDrawerOpen = newState;
  setCallbackValue();
};

export const globalToggleIsNoteDrawerOpen = () => {
  globalIsNoteDrawerOpen = !globalIsNoteDrawerOpen;
  setCallbackValue();
};

const setCallbackValue = () => {
  if (callback) {
    callback(globalIsNoteDrawerOpen);
  }
};

const useNoteTaking = (contentId: string, page: number, user: User | null) => {
  const [isNoteDrawerOpen, setIsNoteDrawerOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string>('');
  const [data, setData] = useState<NoteDto[]>([]);
  const [dataCopy, setDataCopy] = useState<NoteDto[]>([]);
  const [showNoteTaking, setShowNoteTaking] = useState(false);

  const forceHideNoteTaking =
    user &&
    !user.show_experimental_features &&
    user.namespaces.includes('tennessee');

  const setIsNoteDrawerOpenProxy = (newState: boolean) => {
    setIsNoteDrawerOpen(newState);
    globalSetIsNoteDrawerOpen(newState);
  };

  useEffect(() => {
    const localStorageData = localStorage.getItem(LOCAL_STORAGE_SHOW_NOTES_KEY);
    if (localStorageData) {
      try {
        const localStorageShowNoteTaking = JSON.parse(localStorageData);
        setShowNoteTaking(!!localStorageShowNoteTaking);
      } catch (e) {
        setShowNoteTaking(true);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      LOCAL_STORAGE_SHOW_NOTES_KEY,
      JSON.stringify(showNoteTaking),
    );
  }, [showNoteTaking]);

  useEffect(() => {
    callback = (newState: boolean) => {
      setIsNoteDrawerOpen(newState);
    };
  }, []);

  const {
    loading,
    error,
    data: queryData,
  } = useQuery<{
    notes: NoteDto[];
  }>(gql`
    query GetNotes {
      notes {
        id
        data
        content {
          id
        }
        user {
          id
          username
        }
        comment
        updatedAt
      }
    }
  `);

  const [createNote, { data: createData }] = useMutation<{
    createNote: NoteDto;
  }>(gql`
    mutation CreateNote($content: String!, $data: JSON!) {
      createNote(data: { content: $content, data: $data }) {
        id
        data
        content {
          id
        }
        comment
        updatedAt
      }
    }
  `);

  const [deleteNote, { data: deleteData }] = useMutation<any>(gql`
    mutation DeleteNote($id: String!) {
      deleteNote(data: { id: $id }) {
        id
      }
    }
  `);

  const deleteAndRemoveNote = (noteId: string) => {
    deleteNote({
      variables: { id: noteId },
    });
    setData([...data.filter((d) => d.id !== noteId)]);
  };

  const [updateNoteData, { data: updateData }] = useMutation<{
    updateNoteData: NoteDto;
  }>(gql`
    mutation UpdateNoteData($id: String!, $data: JSON!) {
      updateNoteData(data: { id: $id, data: $data }) {
        id
        data
        content {
          id
        }
        comment
        updatedAt
      }
    }
  `);

  useEffect(() => {
    setDataCopy(data);
  }, [data]);

  useEffect(() => {
    if (updateData) {
      const noteIndex = data.findIndex(
        (d) => d.id === updateData.updateNoteData.id,
      );
      if (noteIndex !== -1) {
        dataCopy[noteIndex] = updateData.updateNoteData;
      }
      setDataCopy([...dataCopy]);

      const filteredNotesCopy = dataCopy.filter(
        (d) => d.content.id === contentId && d.data.page === page,
      );

      if (filteredNotesCopy.length) {
        const allNotesDeleted = filteredNotesCopy.reduce((acc, cur) => {
          return acc && cur.data.color === ColorOption.transparent;
        }, true);
        if (allNotesDeleted) {
          Promise.all(
            filteredNotesCopy.map((note) => deleteAndRemoveNote(note.id)),
          ).then(() => {
            // eslint-disable-next-line no-restricted-globals
            location.reload();
          });
        }
      }
    }
  }, [updateData]);

  const [
    updateNoteComment,
    { data: updateComment, loading: isUpdateCommentLoading },
  ] = useMutation<{
    updateNoteComment: NoteDto;
  }>(gql`
    mutation UpdateNoteComment($id: String!, $comment: String!) {
      updateNoteComment(data: { id: $id, comment: $comment }) {
        id
        data
        content {
          id
        }
        comment
        updatedAt
      }
    }
  `);

  useEffect(() => {
    if (queryData && queryData.notes) {
      setData([...queryData.notes]);
    }
  }, [queryData]);

  useEffect(() => {
    if (createData && createData.createNote) {
      data.push(createData.createNote);
      setData([...data]);
    }
  }, [createData]);

  useEffect(() => {
    if (updateComment && updateComment.updateNoteComment) {
      const noteIndex = data.findIndex(
        (d) => d.id === updateComment.updateNoteComment.id,
      );
      if (noteIndex !== -1) {
        data[noteIndex] = updateComment.updateNoteComment;
      }
      setData([...data]);
    }
  }, [updateComment]);

  const filteredNotes = data.filter(
    (d) => d.content.id === contentId && d.data.page === page,
  );

  const notes = filteredNotes.map((a) => ({
    ...a.data,
    comment: a.comment,
    updatedAt: a.updatedAt,
    id: a.id,
  }));

  return {
    data: notes,
    loading,
    error,
    forceHideNoteTaking,
    showNoteTaking: false,
    toggleNoteTaking: () => setShowNoteTaking(!showNoteTaking),
    createNote: (note: any) =>
      createNote({
        variables: { data: { ...note, page }, content: contentId },
      }),
    updateNoteData: (noteId: string, noteData: any) => {
      updateNoteData({
        variables: { id: noteId, data: { ...noteData, page } },
      });
    },
    updateNoteComment: (noteId: string, comment: string) => {
      updateNoteComment({
        variables: { id: noteId, comment },
      });
    },
    deleteNote: deleteAndRemoveNote,
    isUpdateCommentLoading,
    selectedNoteId,
    isOpen: isNoteDrawerOpen,
    onClose: () => setIsNoteDrawerOpenProxy(false),
    onComment: (noteId: string) => {
      setSelectedNoteId(noteId);
      setIsNoteDrawerOpenProxy(true);
    },
  };
};

export default useNoteTaking;
